export const defaultWhitelabelSettings = {
  firm_name: '',
  primary_color: '#2E5BFF',
  accent_color: '#00D4FF',
  logo_url: '',
  disclosure_text: '',
};

export const getWhitelabelSettings = (userRecord) => ({
  ...defaultWhitelabelSettings,
  ...(userRecord?.data?.whitelabel || {}),
});

export const hexToRgb = (hex, fallback = [46, 91, 255]) => {
  if (!hex || typeof hex !== 'string') return fallback;
  const normalized = hex.replace('#', '').trim();
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) return fallback;
  return [
    parseInt(normalized.slice(0, 2), 16),
    parseInt(normalized.slice(2, 4), 16),
    parseInt(normalized.slice(4, 6), 16),
  ];
};

const isSvgUrl = (url) => {
  if (!url) return false;
  return url.startsWith('data:image/svg+xml') || /\.svg($|\?)/i.test(url);
};

const rasterizeSvgImage = (img) => {
  const width = img.naturalWidth || img.width || 1200;
  const height = img.naturalHeight || img.height || 300;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);
  return canvas.toDataURL('image/png');
};

export const loadImageFromUrl = (url) => {
  if (!url) return Promise.resolve(null);
  return new Promise((resolve) => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (isSvgUrl(url)) {
        try {
          resolve({
            src: rasterizeSvgImage(img),
            width: img.naturalWidth || img.width,
            height: img.naturalHeight || img.height,
            format: 'PNG',
          });
          return;
        } catch (error) {
          console.error('Error rasterizing SVG logo:', error);
          resolve(null);
          return;
        }
      }
      resolve({
        src: img,
        width: img.naturalWidth || img.width,
        height: img.naturalHeight || img.height,
        format: url.startsWith('data:image/jpeg') || /\.jpe?g($|\?)/i.test(url) ? 'JPEG' : 'PNG',
      });
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
};

export const drawPdfHeader = async (pdf, {
  pageWidth,
  title,
  logoImg,
  logoText = 'SharpeMetrix',
  primaryColor = '#2E5BFF',
  headerHeight = 28,
}) => {
  const [r, g, b] = hexToRgb(primaryColor);
  pdf.setFillColor(r, g, b);
  pdf.rect(0, 0, pageWidth, headerHeight, 'F');

  if (logoImg) {
    const ratio = logoImg.width && logoImg.height ? logoImg.width / logoImg.height : 5;
    const maxLogoWidth = 58;
    const maxLogoHeight = 12;
    let logoWidth = maxLogoWidth;
    let logoHeight = logoWidth / ratio;
    if (logoHeight > maxLogoHeight) {
      logoHeight = maxLogoHeight;
      logoWidth = logoHeight * ratio;
    }
    const logoY = (headerHeight - logoHeight) / 2 + 1;
    pdf.addImage(logoImg.src || logoImg, logoImg.format || 'PNG', 15, logoY, logoWidth, logoHeight);
  } else {
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(255, 255, 255);
    pdf.text(logoText || 'SharpeMetrix', 15, headerHeight / 2 + 5);
  }

  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(255, 255, 255);
  const maxTitleWidth = pageWidth - 95;
  const displayTitle = pdf.getTextWidth(title) > maxTitleWidth
    ? `${title.slice(0, 34)}...`
    : title;
  pdf.text(displayTitle, pageWidth - 15, headerHeight / 2 + 5, { align: 'right' });
};

export const addPdfFinalText = (pdf, {
  pageWidth,
  pageHeight,
  title = 'Additional Notes',
  text,
  primaryColor = '#2E5BFF',
}) => {
  if (!text?.trim()) return;
  const [r, g, b] = hexToRgb(primaryColor);
  pdf.addPage();
  let y = 25;
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(r, g, b);
  pdf.text(title, 15, y);
  y += 10;
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(50, 50, 50);
  const lines = pdf.splitTextToSize(text, pageWidth - 30);
  pdf.text(lines, 15, y);
};

export const addPdfPageNumbers = (pdf, pageWidth, pageHeight) => {
  const totalPages = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    pdf.setFontSize(8);
    pdf.setTextColor(100, 100, 100);
    pdf.text(`Page ${i} of ${totalPages}`, pageWidth - 30, pageHeight - 10);
  }
};
